/**
 * useChatMode.js
 *
 * Exposes the chat mode (international vs domestic) for a contact.
 *
 * The chat mode is derived from the contact's phone number on the backend and
 * returned only on page 1 of the paginated messages endpoint. `useLiveChat`
 * primes the React Query cache (key: ['chatMode', number]) when it loads page 1,
 * so this hook normally reads straight from cache and never refetches while the
 * user scrolls older messages. The queryFn is only a fallback for the rare case
 * where the footer mounts before the conversation's page 1 has been cached.
 *
 * Usage:
 *   const { data: chatMode } = useChatMode(number);
 *   if (chatMode?.is_international) { ...pass int=true... }
 */

import { useQuery } from '@tanstack/react-query';
import apiClient from 'services/apiClient';
import { useMyContext } from 'Context/MyContextProvider';

export const chatModeKeys = {
  byNumber: (number) => ['chatMode', String(number)],
};

export const useChatMode = (number, options = {}) => {
  const { api } = useMyContext();

  return useQuery({
    queryKey: chatModeKeys.byNumber(number),
    queryFn: async () => {
      const res = await apiClient.get(`${api}message/${number}/paginated`, {
        params: { page: 1, per_page: 1 },
      });
      const data = res.data || {};
      return {
        is_international: Boolean(data.is_international),
        country_iso2: data.country_iso2 ?? null,
        flag: data.flag ?? null,
        country_name: data.country_name ?? null,
      };
    },
    enabled: !!number,
    // Chat mode never changes for a given number — keep it cached indefinitely
    // so scrolling/pagination never triggers a refetch or state change.
    staleTime: Infinity,
    gcTime: Infinity,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    ...options,
  });
};
